import { useState } from 'react'

import { t } from '../../../../common/i18n.js'
import { Language } from '../../../../common/types/i18n.js'
import type { BalanceTimeSummary } from '../../../../common/types/playtime.js'
import {
    DateRangeItem,
    groupConsecutiveDateItems,
} from '../../../../common/utils/date-range-group.js'
import { formatWorkload, humanReadableHours } from '../../../../common/utils/format-utils.js'
import { Calendar, CalendarHeart, Clock } from '../../../components/icon/solar-icons.js'
import { Tooltip } from '../../../components/tooltip/tooltip.js'
import '../../portal/views/dashboard/card-frame.css'

// Official source on how the state computes the work-time norm (Tööinspektsioon).
const WORKTIME_SOURCE_URL = {
    et: 'https://www.tooelu.ee/et/24/tooaeg',
    en: 'https://www.tooelu.ee/en/24/working-time',
}

export interface BalanceTimeSummaryProps {
    summary: BalanceTimeSummary
    /** Subject's current workload (koormus); null falls back to full-time. */
    fte: number | null
    /** Period end (ISO) — the expected-by-today marker only renders while it's in the future. */
    displayEndDate: string
    lang: Language
}

export const BalanceTimeSummaryCards = ({
    summary,
    fte,
    displayEndDate,
    lang,
}: BalanceTimeSummaryProps) => {
    const bt = t.playtime.balancetime
    const deltaClass =
        summary.delta > 0
            ? 'portal-card__value--positive'
            : summary.delta < 0
              ? 'portal-card__value--negative'
              : ''

    const daysLabel = (count: number) => (count === 1 ? bt.day(lang) : bt.days(lang))

    const hours = (value: number) => humanReadableHours(value)

    // '6h 20m vähem' / '1h 00m rohkem' — the direction word carries the sign
    // ('-6h vähem' would read as a double negative, i.e. more).
    const deltaText =
        summary.delta === 0
            ? humanReadableHours(0)
            : `${humanReadableHours(Math.abs(summary.delta))} ${
                  summary.delta < 0 ? bt.deltaLess(lang) : bt.deltaMore(lang)
              }`

    // Third column: tracked vs the WHOLE period's expectation (the first
    // column), not vs today's — rounded to avoid binary float dust.
    const diffFromTotal =
        Math.round((summary.totalTrackedHours - summary.totalExpectedHours) * 100) / 100
    const diffClass =
        diffFromTotal > 0
            ? 'portal-card__value--positive'
            : diffFromTotal < 0
              ? 'portal-card__value--negative'
              : ''

    // The expected-by-today marker only makes sense while the period is still
    // running — for closed periods the whole bar is the expectation.
    const now = new Date()
    const todayIso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate(),
    ).padStart(2, '0')}`
    const isCurrentPeriod = displayEndDate >= todayIso

    const workload = fte ?? 1
    const expectedHeader =
        workload === 1
            ? bt.expectedTotalFull(formatWorkload(workload, lang))(lang)
            : bt.expectedTotalPartial(formatWorkload(workload, lang))(lang)

    // Which card's breakdown tooltip is open (only one at a time).
    const [openTip, setOpenTip] = useState<string | null>(null)
    const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`

    // '20.02' for a single day, '20.02–27.02' for a merged run of days.
    const ddmmRange = (r: DateRangeItem) =>
        r.startDate === r.endDate ? ddmm(r.startDate) : `${ddmm(r.startDate)}–${ddmm(r.endDate)}`

    // Renders the breakdown behind a count as a hover tooltip (an info icon next
    // to the card title). Nothing renders when there's nothing behind it.
    const breakdownTip = (key: string, items: DateRangeItem[]) =>
        items.length > 0 ? (
            <Tooltip
                isShown={openTip === key}
                onToggle={(shown) => setOpenTip(shown ? key : null)}
                triggerEvent="hover"
                placement="top"
                triggerClassName="balancetime__card-tip"
            >
                <ul className="balancetime__tip-list">
                    {items.map((it) => (
                        <li key={it.startDate}>
                            <span className="balancetime__tip-date">{ddmmRange(it)}</span>
                            {it.text}
                        </li>
                    ))}
                </ul>
            </Tooltip>
        ) : null

    // The formula behind the expected-hours total: full-time-scale norm minus
    // deductions, workload applied after them, then the flat shortened-day cut
    // last — mirrors how the state computes the work-time norm (see
    // WORKTIME_SOURCE_URL). Click-triggered so the official-source link stays
    // reachable.
    const breakdown = summary.expectedBreakdown
    const dayHoursLabel = (value: number) =>
        Number.isInteger(value) ? `${value}h` : humanReadableHours(value)
    const deductionRow = (label: string, value: number) =>
        value > 0 ? (
            <div className="balancetime__formula-row">
                <span>− {label}</span>
                <span>{humanReadableHours(value)}</span>
            </div>
        ) : null
    const formulaTip = breakdown ? (
        <Tooltip
            isShown={openTip === 'formula'}
            onToggle={(shown) => setOpenTip(shown ? 'formula' : null)}
            triggerEvent="click"
            placement="top"
            className="balancetime__formula-popper"
            triggerClassName="balancetime__value-tip"
        >
            <div className="balancetime__formula">
                <div className="balancetime__formula-row">
                    <span>
                        {bt.formulaNorm(
                            breakdown.workdays,
                            dayHoursLabel(breakdown.fullTimeDayHours),
                        )(lang)}
                    </span>
                    <span>{humanReadableHours(breakdown.normHours)}</span>
                </div>
                {deductionRow(bt.publicHolidays(lang), breakdown.publicHolidayHours)}
                {deductionRow(bt.timeOff(lang), breakdown.timeOffHours)}
                {deductionRow(bt.eventDays(lang), breakdown.eventDayHours)}
                <div className="balancetime__formula-row">
                    <span>× {bt.formulaWorkload(lang)}</span>
                    <span>{formatWorkload(breakdown.fte, lang)}</span>
                </div>
                {deductionRow(bt.formulaShortenedDays(lang), breakdown.shortenedDayHours)}
                {breakdown.fte === 0 ? (
                    <div className="balancetime__formula-note">{bt.formulaNotTracked(lang)}</div>
                ) : null}
                <div className="balancetime__formula-row balancetime__formula-row--total">
                    <span>= {bt.formulaTotal(lang)}</span>
                    <span>{humanReadableHours(summary.totalExpectedHours)}</span>
                </div>
                <a
                    className="balancetime__formula-source"
                    href={WORKTIME_SOURCE_URL[lang]}
                    target="_blank"
                    rel="noreferrer"
                >
                    {bt.formulaSource(lang)}
                </a>
            </div>
        </Tooltip>
    ) : null

    // Same-type runs collapse into ranges. Public holidays bridge time-off/event
    // runs (like weekends, they carry no item of their own), so a booking spanning
    // one stays a single row.
    const holidayDates = new Set((summary.publicHolidayItems ?? []).map((h) => h.date))
    const holidayTip = groupConsecutiveDateItems(
        (summary.publicHolidayItems ?? []).map((h) => ({ date: h.date, text: h.name })),
    )
    const timeOffTip = groupConsecutiveDateItems(
        (summary.timeOffItems ?? []).map((it) => ({ date: it.date, text: it.label })),
        holidayDates,
    )
    const eventTip = groupConsecutiveDateItems(
        (summary.eventDayItems ?? []).map((e) => ({ date: e.date, text: e.name })),
        holidayDates,
    )

    return (
        <div className="balancetime__content">
            {/* Summary cards row */}
            <div className="balancetime__cards">
                {/* Delta / overtime card */}
                <div className="portal-card">
                    <div className="portal-card__header">
                        <div className="portal-card__icon">
                            <Clock size={20} />
                        </div>
                        <div className="portal-card__title">{bt.deltaSummary(lang)}</div>
                    </div>
                    <div className={`portal-card__value ${deltaClass}`}>{deltaText}</div>
                    <p className="portal-card__subtext">{bt.deltaSummaryDesc(lang)}</p>
                </div>

                {/* Public holidays card */}
                <div className="portal-card">
                    <div className="portal-card__header">
                        <div className="portal-card__icon">
                            <Calendar size={20} />
                        </div>
                        <div className="portal-card__title">{bt.publicHolidays(lang)}</div>
                        {breakdownTip('holidays', holidayTip)}
                    </div>
                    <div className="portal-card__value">
                        {summary.totalPublicHolidays} {daysLabel(summary.totalPublicHolidays)}
                    </div>
                    <p className="portal-card__subtext">{bt.publicHolidaysDesc(lang)}</p>
                </div>

                {/* Time off card */}
                <div className="portal-card">
                    <div className="portal-card__header">
                        <div className="portal-card__icon">
                            <Calendar size={20} />
                        </div>
                        <div className="portal-card__title">{bt.timeOff(lang)}</div>
                        {breakdownTip('timeOff', timeOffTip)}
                    </div>
                    <div className="portal-card__value">
                        {summary.totalTimeOffDays} {daysLabel(summary.totalTimeOffDays)}
                    </div>
                    <p className="portal-card__subtext">{bt.timeOffDesc(lang)}</p>
                </div>

                {/* Event days card */}
                <div className="portal-card">
                    <div className="portal-card__header">
                        <div className="portal-card__icon">
                            <CalendarHeart size={20} />
                        </div>
                        <div className="portal-card__title">{bt.eventDays(lang)}</div>
                        {breakdownTip('events', eventTip)}
                    </div>
                    <div className="portal-card__value">
                        {summary.totalEventDays} {daysLabel(summary.totalEventDays)}
                    </div>
                    <p className="portal-card__subtext">{bt.eventDaysDesc(lang)}</p>
                </div>
            </div>

            {/* Summary table */}
            <div className="portal-card">
                <table className="balancetime__table">
                    <thead>
                        <tr>
                            <th>{expectedHeader}</th>
                            <th>{bt.tracked(lang)}</th>
                            <th>{bt.differenceFromTotal(lang)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <span className="balancetime__expected-value">
                                    {hours(summary.totalExpectedHours)}
                                    {formulaTip}
                                </span>
                            </td>
                            <td>{hours(summary.totalTrackedHours)}</td>
                            <td className={diffClass}>{hours(diffFromTotal)}</td>
                        </tr>
                    </tbody>
                </table>

                {/* Progress bar */}
                <div className="balancetime__progress">
                    <div className="balancetime__progress-bar">
                        <div className="balancetime__progress-bg" />
                        <div
                            className="balancetime__progress-indicator"
                            style={{ width: `${summary.progressBarPercentage}%` }}
                        >
                            {(
                                summary.progressBarPercentageLabel ?? summary.progressBarPercentage
                            ).toFixed(1)}
                            %
                        </div>
                        {isCurrentPeriod && (
                            <div
                                className={`balancetime__progress-line ${deltaClass}`}
                                style={{ width: `${summary.progressBarExpectedPercentage}%` }}
                            />
                        )}
                    </div>
                    {isCurrentPeriod && (
                        <div className="balancetime__progress-caption">
                            <span
                                className="balancetime__progress-caption-text"
                                style={{
                                    left: `${summary.progressBarExpectedPercentage}%`,
                                    // Slides from left-aligned at 0% to
                                    // right-aligned at 100% so the label never
                                    // overflows the card.
                                    transform: `translateX(-${summary.progressBarExpectedPercentage}%)`,
                                }}
                            >
                                {bt.expectedTodayMarker(lang)}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
